import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionSummary, AccordionDetails, Stack } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { IconButton, Tooltip, Typography, theme } from 'cprime-foundational-components';
import { filter } from 'lodash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import * as S from './CascadingFiltersHeader.styles.jsx';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper.jsx';

export const CascadingFiltersHeader = ({
  isDisabled = false,
  disabledTooltip,
  headerName,
  type,
  options,
  setOpenSection,
  openSection,
  setListSectionData,
  setCascadingFiltersLocal,
  cascadingFiltersLocal,
  children,
  getChipLabel,
  error,
  ...cascadingFiltersObj
}) => {
  const openAccordion = () => setOpenSection(headerName === openSection ? null : headerName);

  const accordionOnClickHandler = () => {
    if (setListSectionData) {
      setListSectionData({ headerName, options });
    } else {
      openAccordion();
    }
  };

  const handleDelete = () => {
    let field;
    if (type === 'CascadingFiltersHeader') {
      field = cascadingFiltersObj.field;
    } else {
      field = options[0].field;
    }
    if (field) {
      const newCascadingFiltersLocal = filter(
        cascadingFiltersLocal,
        (filterLocal) => filterLocal.field !== field,
      );
      setCascadingFiltersLocal(newCascadingFiltersLocal);
    }
  };

  return (
    <Tooltip
      title={
        isDisabled
          ? disabledTooltip ||
            'Unable to select because an accessory or color has been selected. Remove selection to select another.'
          : ''
      }
      placement="bottom"
    >
      <Accordion
        square={true}
        disableGutters={true}
        expanded={headerName === openSection}
        onChange={() => {
          if (isDisabled) {
            return;
          }
          accordionOnClickHandler();
        }}
        sx={{
          '&.css-1betqn-MuiAccordionSummary-content': { margin: '0 0 35px' },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          minHeight: '2.5rem',
          '&.MuiPaper-rounded': {
            boxShadow: 0,
            transition: 'none',
          },
          '&.MuiPaper-root': {
            boxShadow: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            type === 'CascadingFiltersHeader' ? (
              <IconButton
                size="extraSmall"
                sx={{
                  p: 0.25,
                  '&:hover': {
                    backgroundColor: isDisabled ? 'transparent' : undefined,
                  },
                }}
              >
                <ArrowForwardIcon
                  sx={{
                    width: '1rem',
                    height: '1rem',
                    color: isDisabled ? theme.colors.secondary[60] : theme.colors.common.black,
                  }}
                />
              </IconButton>
            ) : (
              <IconButton size="extraSmall" sx={{ p: 0.25 }}>
                <ExpandMoreIcon sx={{ width: '1rem', height: '1rem' }} />
              </IconButton>
            )
          }
          aria-controls="panel-content"
          id="panel-header"
          margin={0}
          sx={{
            minHeight: '2.5rem',
            height: '2.5rem',
            '& .MuiAccordionSummary-content': { minWidth: 0 },
            boxShadow: 0,
          }}
        >
          <Stack direction="row" alignItems="center" width="100%" gap={1}>
            <Typography
              color={isDisabled ? theme.colors.secondary[60] : theme.colors.common.black}
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {headerName}
            </Typography>

            {error?.[headerName] ? (
              <S.StyledChip
                backgroundColor={theme.colors.error[0]}
                color={theme.colors.error[70]}
                label={
                  <TooltipWrapper tooltipText="Action Needed to Apply filter">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <ErrorIcon fontSize="small" />
                      {getChipLabel({ headerName, options })}
                    </Stack>
                  </TooltipWrapper>
                }
                sx={{ border: `0.0625rem solid ${theme.colors.error[70]}` }}
              />
            ) : (
              <S.StyledChip
                disabled={isDisabled ? isDisabled : false}
                backgroundColor={options?.find((option) => option.field === 'timePeriod')?.bgColor}
                label={getChipLabel({ headerName, options })}
                onDelete={
                  !options?.find((option) => option.field === 'timePeriod') &&
                  getChipLabel({ headerName, options }) !== 'ALL'
                    ? handleDelete
                    : undefined
                }
                sx={{
                  '&, *': {
                    cursor: 'pointer',
                  },
                }}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '0 0 0.5rem' }}>{children}</AccordionDetails>
      </Accordion>
    </Tooltip>
  );
};

CascadingFiltersHeader.propTypes = {
  cascadingFiltersLocal: PropTypes.array,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  error: PropTypes.object,
  disabledTooltip: PropTypes.string,
  isDisabled: PropTypes.bool,
  getChipLabel: PropTypes.func.isRequired,
  headerName: PropTypes.string,
  openSection: PropTypes.string,
  options: PropTypes.array,
  setCascadingFiltersLocal: PropTypes.func,
  setListSectionData: PropTypes.func,
  setOpenSection: PropTypes.func,
  type: PropTypes.string,
};
